import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import _without from 'lodash/without';
import { translator, selectors as i18nSelectors } from 'mage-i18n';
import {
  ButtonSecondary,
  Typography,
  SpacerHorizontal,
  SpacerVertical,
  Strong,
} from '@klarna/bubble-ui';
import { analytics, defaultContainerPropTypes, HorizontalWrapper, Selector } from 'moet-core';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { ContainerTemplate, withLeadUpdateOnNext } from '@/components';
import { getVolumeSeparationConfig } from '@/selectors/data';
import * as a from '@/actions/signup';

import { ANALYTICS_EVENTS } from './analyticsEvents';
import { CY_VOLUME_SEPARATION_CONTAINER_IDS } from './constants';

const tPrefix = 'products-fe.containers.volume_separation';

export class VolumeSeparation extends React.Component {
  constructor(props) {
    super(props);

    this.onSelect = this.onSelect.bind(this);
    this.prepareFields = this.prepareFields.bind(this);

    const options = this.prepareFields();

    this.state = { options, loading: false, language: props.language };
  }

  prepareFields() {
    const {
      currencyFormatterFactory,
      volumeSeparationConfig: { currency, skipMedium, large = 0, medium = 0 },
    } = this.props;
    const currencyFormatter = currencyFormatterFactory(currency, true);
    const smallOption = {
      // when `skipMedium` we treat merchant as small business merchant
      value: `${medium}`,
      label: this.props.t(`${tPrefix}.below`, {
        threshold: currencyFormatter.format(
          // when `skipMedium`, we show below `large` and above `large` as options
          skipMedium ? large : medium,
        ),
      }),
      tier: 'small',
      ...generateCyElemId(CY_VOLUME_SEPARATION_CONTAINER_IDS.VOLUME('BELOW'), window),
    };
    const mediumOption = !skipMedium
      ? {
          value: `${(medium + large) / 2}`,
          label: this.props.t(`${tPrefix}.between`, {
            belowThreshold: currencyFormatter.format(medium),
            aboveThreshold: currencyFormatter.format(large),
          }),
          tier: 'medium',
          ...generateCyElemId(CY_VOLUME_SEPARATION_CONTAINER_IDS.VOLUME('BETWEEN'), window),
        }
      : null;
    const largeOption = {
      value: `${large}`,
      label: this.props.t(`${tPrefix}.over`, {
        threshold: currencyFormatter.format(large),
      }),
      tier: 'large',
      ...generateCyElemId(CY_VOLUME_SEPARATION_CONTAINER_IDS.VOLUME('ABOVE'), window),
    };

    return _without([smallOption, mediumOption, largeOption], null);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.language !== this.props.language) {
      const options = this.prepareFields();
      /* eslint-disable-next-line react/no-did-update-set-state */
      this.setState({ options, language: this.props.language });
    }
  }

  onSelect(value) {
    if (this.state.loading) {
      return;
    }

    this.setState({ loading: true });

    const selected = this.state.options.find(o => o.value === value);

    if (selected) {
      const { onNext, onSelectRevenue } = this.props;
      const annualRevenue = selected.value;
      const annualRevenueTier = selected.tier;

      onNext({
        leadData: { annualRevenue },
        onNextCallback: () => {
          analytics.logEvent(ANALYTICS_EVENTS.volumeSeparation, {
            annualRevenue,
            annualRevenueTier,
          });
          onSelectRevenue(annualRevenue);
        },
      }).catch(() => this.setState({ loading: false }));
    }
  }

  render() {
    const { isDisabled, onBack, t } = this.props;
    const { loading } = this.state;

    return (
      <ContainerTemplate
        title={t(`${tPrefix}.title_line_1`) + ' ' + t(`${tPrefix}.title_line_2`)}
        subtitle={t(`${tPrefix}.subtitle`)}
        showRecaptchaFooter
      >
        <Typography textToken="text-style/text/super-paragraphs/tall/regular">
          <Strong>{t(`${tPrefix}.selector_title`)}</Strong>
        </Typography>
        <SpacerVertical small />
        <Selector
          id="volume_separation--selector"
          disabled={isDisabled}
          loading={loading}
          options={this.state.options}
          onSelect={this.onSelect}
        />
        <SpacerVertical large />
        <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-start">
          <ButtonSecondary
            onClick={onBack}
            disabled={isDisabled}
            {...generateCyElemId(CY_VOLUME_SEPARATION_CONTAINER_IDS.BTN_BACK, window)}
          >
            {t(`${tPrefix}.buttons.back`)}
          </ButtonSecondary>
          <SpacerHorizontal medium />
        </HorizontalWrapper>
      </ContainerTemplate>
    );
  }
}

VolumeSeparation.propTypes = {
  ...defaultContainerPropTypes,
  language: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  volumeSeparationConfig: PropTypes.shape({
    AO: PropTypes.arrayOf(PropTypes.string).isRequired,
    currency: PropTypes.string.isRequired,
    large: PropTypes.number.isRequired,
    medium: PropTypes.number.isRequired,
    skipMedium: PropTypes.bool,
  }),
};

export default compose(
  connect(
    state => ({
      volumeSeparationConfig: getVolumeSeparationConfig(state),
      currencyFormatterFactory: i18nSelectors.formatters.currency(state),
      language: i18nSelectors.language(state),
      t: translator(state),
    }),
    dispatch => ({
      onSelectRevenue: annualRevenue =>
        a.onChangeFormValue('annualRevenue', annualRevenue)(dispatch),
    }),
  ),
  withLeadUpdateOnNext,
)(VolumeSeparation);
